import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteCategories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const isHome = location.pathname === '/';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHome
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <span className="text-xl font-heading text-foreground-950">
                Kakobuy<span className="text-primary-500">Spreadsheet</span>
              </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/"
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  isHome
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-600 hover:text-primary-500 hover:bg-background-100'
                }`}
              >
                Strona główna
              </Link>
              <Link
                to="/artykul/kakobuy-spreadsheet-przewodnik"
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors"
              >
                Przewodnik zakupowy
              </Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <span>Kategorie</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                    {siteCategories.map((cat) => (
                      <a
                        key={cat.nameEn}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-primary-500`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-700"
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile slide-out menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={closeMobileMenu}></div>
          <div className="absolute top-0 right-0 w-80 max-w-[85vw] h-full bg-background-50 overflow-y-auto pt-20 pb-8 px-5">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  isHome ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-line"></i>
                </span>
                Strona główna
              </Link>

              <div className="border-t border-background-200/70 my-2 pt-2">
                <p className="px-3 py-2 text-xs text-foreground-500 font-medium uppercase tracking-wider">Przewodniki</p>
                <Link
                  to="/artykul/kakobuy-spreadsheet-przewodnik"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-book-open-line text-primary-500"></i>
                  </span>
                  Przewodnik zakupowy
                </Link>
              </div>

              <div className="border-t border-background-200/70 my-2 pt-2">
                <p className="px-3 py-2 text-xs text-foreground-500 font-medium uppercase tracking-wider">Kategorie produktów</p>
                {siteCategories.map((cat) => (
                  <a
                    key={cat.nameEn}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
                    onClick={closeMobileMenu}
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100/80 text-primary-500">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}